import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, CalendarRange, FileEdit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, ownDancerIdParam } from "../../lib/api";
import { API_CACHE_TTL, cachedGet, invalidateFeedCache } from "../../lib/apiCache";
import { useGuestIdentity } from "../../lib/guestIdentity";
import { eventPublicPath } from "../../lib/eventLinks";
import DangerConfirmModal from "../DangerConfirmModal";
import { Button, Card, EmptyState } from "../ui";
import ProfileLoadingSkeleton from "../profile/ProfileLoadingSkeleton";
import {
  FieldHint,
  FORM_STYLE_CARD_CLASS,
  SectionTitle,
} from "../ui/FormSection";

const LIST_LINK =
  "block rounded-md2 border border-black/[0.08] bg-white px-4 py-3 min-h-[44px] flex items-center justify-between gap-3 font-ui font-semibold text-[14px] hover:bg-cream active:bg-cream/70 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2";

function formatWhen(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDraftEdited(iso) {
  if (!iso) return "Recently";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Recently";
  }
}

/**
 * Host Events tab — list hosted events, drafts, + create CTA.
 */
export default function ProfileHostEventsPanel({ account }) {
  const { dancerId } = useGuestIdentity();
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [draftToDelete, setDraftToDelete] = useState(null);

  const loadDrafts = useCallback(async ({ force = false } = {}) => {
    try {
      const { data } = await cachedGet("/feed", {
        params: { scope: "drafts", dancer_id: ownDancerIdParam(dancerId) },
        ttlMs: API_CACHE_TTL.FEED_DRAFTS,
        force,
      });
      return data.drafts || [];
    } catch {
      return [];
    }
  }, [dancerId]);

  useEffect(() => {
    if (!account) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [eventsRes, draftList] = await Promise.all([
          api.get("/organizers/me/events"),
          loadDrafts(),
        ]);
        if (!cancelled) {
          setItems(eventsRes.data?.items || []);
          setDrafts(draftList);
        }
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account, loadDrafts]);

  async function confirmDeleteDraft() {
    if (!draftToDelete) return;
    setDeletingId(draftToDelete);
    try {
      await api.delete(`/events/${draftToDelete}`);
      await invalidateFeedCache();
      const list = await loadDrafts({ force: true });
      setDrafts(list);
      setDraftToDelete(null);
      toast.success("Draft deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-6" data-testid="profile-host-events-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-ui font-semibold text-[18px] sm:text-[20px] leading-snug text-balance">
            Your events
          </h2>
          <FieldHint className="mt-1 text-[13px]">
            Socials and workshops you host, plus unpublished drafts.
          </FieldHint>
        </div>
        {!loading && items.length > 0 ? (
          <Button
            as={Link}
            to="/create"
            variant="primary"
            data-testid="profile-create-event-btn"
            className="shrink-0"
          >
            <CalendarPlus className="w-4 h-4" strokeWidth={2} aria-hidden />
            Create event
          </Button>
        ) : null}
      </div>

      {loading ? (
        <ProfileLoadingSkeleton label="Loading your events…" variant="list" />
      ) : items.length === 0 && drafts.length === 0 ? (
        <EmptyState
          compact
          icon={<CalendarRange className="w-8 h-8" strokeWidth={1.5} aria-hidden />}
          title="No events yet"
          description="Publish a social or workshop — share the invite link when you're ready."
          action={
            <Button as={Link} to="/create" variant="primary" data-testid="profile-create-event-empty-btn">
              <CalendarPlus className="w-4 h-4" strokeWidth={2} aria-hidden />
              Create event
            </Button>
          }
          data-testid="profile-host-events-empty"
        />
      ) : (
        <>
          {items.length > 0 ? (
            <Card className={FORM_STYLE_CARD_CLASS}>
              <SectionTitle icon={CalendarRange}>Upcoming & live</SectionTitle>
              <ul className="mt-3 space-y-2">
                {items.map((ev) => {
                  const when = formatWhen(
                    ev.starts_at
                      || (typeof ev.occurrences?.[0] === "string" ? ev.occurrences[0] : ev.occurrences?.[0]?.starts_at)
                  );
                  const path = ev.public_path || eventPublicPath(ev) || `/dashboard/${ev.id}`;
                  return (
                    <li key={ev.id}>
                      <Link
                        to={path}
                        className={LIST_LINK}
                        data-testid={`profile-hosted-event-${ev.id}`}
                      >
                        <span className="truncate">{ev.name}</span>
                        <span className="text-[12px] font-medium text-ink/70 shrink-0">
                          {when || ev.city || "Event"}
                        </span>
                      </Link>
                      <div className="mt-1.5 mb-2 flex flex-wrap gap-2 px-1">
                        <Button
                          as={Link}
                          to={`/dashboard/${ev.id}`}
                          variant="link"
                          size="sm"
                          data-testid={`profile-hosted-dashboard-${ev.id}`}
                        >
                          Dashboard
                        </Button>
                        <Button
                          as={Link}
                          to={`/e/${ev.id}/edit`}
                          variant="link"
                          size="sm"
                          data-testid={`profile-hosted-edit-${ev.id}`}
                        >
                          Edit
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : (
            <EmptyState
              compact
              icon={<CalendarRange className="w-8 h-8" strokeWidth={1.5} aria-hidden />}
              title="No published events"
              description="Finish a draft below, or create a new social."
              action={
                <Button as={Link} to="/create" variant="primary" data-testid="profile-create-event-empty-btn">
                  <CalendarPlus className="w-4 h-4" strokeWidth={2} aria-hidden />
                  Create event
                </Button>
              }
              data-testid="profile-host-events-empty"
            />
          )}

          {drafts.length > 0 ? (
            <Card className={FORM_STYLE_CARD_CLASS} data-testid="profile-drafts">
              <SectionTitle icon={FileEdit}>Drafts</SectionTitle>
              <ul className="mt-3 space-y-2">
                {drafts.map((draft) => {
                  const title = draft.name?.trim() || "Untitled draft";
                  const when = formatWhen(draft.starts_at);
                  return (
                    <li key={draft.id}>
                      <Link
                        to={`/e/${draft.id}/edit`}
                        className={LIST_LINK}
                        data-testid={`profile-draft-${draft.id}`}
                      >
                        <span className="truncate">{title}</span>
                        <span className="text-[12px] font-medium text-ink/70 shrink-0">
                          {when || "No date yet"}
                        </span>
                      </Link>
                      <div className="mt-1.5 mb-2 flex flex-wrap items-center gap-2 px-1">
                        <span className="text-[12px] font-ui font-medium text-ink/55">
                          Last edited {formatDraftEdited(draft.updated_at)}
                        </span>
                        <Button
                          as={Link}
                          to={`/e/${draft.id}/edit`}
                          variant="link"
                          size="sm"
                          data-testid={`profile-draft-edit-${draft.id}`}
                        >
                          Continue editing
                        </Button>
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          disabled={deletingId === draft.id}
                          onClick={() => setDraftToDelete(draft.id)}
                          data-testid={`profile-draft-delete-${draft.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                          {deletingId === draft.id ? "Deleting…" : "Delete"}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : null}
        </>
      )}

      <DangerConfirmModal
        open={Boolean(draftToDelete)}
        title="Delete this draft?"
        message="This permanently removes the draft. It can't be undone."
        confirmLabel="Delete draft"
        onClose={() => !deletingId && setDraftToDelete(null)}
        onConfirm={confirmDeleteDraft}
        busy={Boolean(deletingId)}
        requireYes
        testId="delete-draft-modal"
      />
    </div>
  );
}
